// §9.3 — the shell↔app `postMessage` protocol.
//
// Every message on the wire is an `Envelope`, and every envelope's `payload` is validated against
// the schema for its `(ch, type)` pair. Nothing here trusts anything: the bridge host (M8.3) checks
// the *origin* and the *token* before this file is even reached, and this file then refuses to hand
// the kernel a payload it has not checked the shape of.
//
// The two directions are separate tables. `window/title` is app→shell and `window/focus` is
// shell→app; a table that merged them would let an app send itself a `focus` it was never given.
import { z } from 'zod';
import { menuNodeSchema } from './manifest';

export const BRIDGE_PROTOCOL_VERSION = 1 as const;

/** §9.3 — the closed channel set. */
export const CHANNELS = [
  'handshake',
  'window',
  'menu',
  'command',
  'fs',
  'transfer',
  'notify',
  'dialog',
  'session',
  'auth',
  'exec',
  'keys',
] as const;
export type Channel = (typeof CHANNELS)[number];

export const channelSchema = z.enum(CHANNELS);

/** §9.3 — every message on the wire, in both directions. */
export const envelopeSchema = z.object({
  v: z.literal(BRIDGE_PROTOCOL_VERSION),
  id: z.string().min(1),
  ch: channelSchema,
  type: z.string().min(1),
  payload: z.unknown(),
  token: z.string().min(1),
});

export type Envelope = z.infer<typeof envelopeSchema>;

/** §9.3 — an app has 3 s to complete the handshake before it is left unbridged (F7). */
export const HANDSHAKE_TIMEOUT_MS = 3_000;

/** §9.3 — capability tokens are 256 bits, minted per window instance and never reused. */
export const CAPABILITY_TOKEN_BITS = 256;

// ── Shared payload pieces ──────────────────────────────────────────────────────────────────

/** A reply carries the id of the message it answers, so a caller can await one. */
export const replySchema = z.object({ to: z.string().min(1) }).loose();

const okOrError = z.object({
  to: z.string().min(1),
  ok: z.boolean(),
  /** Present when `ok` is false: `HangarApiError`'s code and message (§4.1). */
  code: z.string().optional(),
  message: z.string().optional(),
  value: z.unknown().optional(),
});

/** §10 — a transfer payload, in its on-the-wire form. Bytes travel base64; text travels as text. */
export const wirePayloadSchema = z.object({
  items: z
    .array(
      z.object({
        mime: z.string().min(1),
        /** base64, because a structured-clone `Uint8Array` is not what every host can send. */
        bytes: z.string().optional(),
        text: z.string().optional(),
        path: z.string().optional(),
      }),
    )
    .min(1),
});

// §9.3 `menu/publish` carries the same tree a manifest's static default does (§9.2).
const menuNodeWireSchema = menuNodeSchema;

/** §7.1's `FileEntry`, as it travels. `fs/changed` carries one for a create or a modify. */
const fileEntryWireSchema = z.object({
  path: z.string().min(1),
  name: z.string(),
  isDir: z.boolean(),
  size: z.number(),
  mtime: z.number(),
  rev: z.number(),
});

// ── app → shell ────────────────────────────────────────────────────────────────────────────

/**
 * §9.3's table, read left to right. The key is `<channel>/<type>`; the value validates `payload`.
 *
 * `fs` is one entry rather than twelve because §4.1 is the contract for what those calls *are* —
 * the bridge's job is to establish that a `call` names a method and carries arguments, and the api
 * host's is to reject a method that does not exist or arguments that are wrong (M8.4).
 */
export const APP_TO_SHELL = {
  'handshake/hello': z.object({
    appId: z.string().min(1),
    version: z.string().min(1),
    channels: z.array(channelSchema),
  }),

  'window/title': z.object({ title: z.string() }),
  'window/icon': z.object({ svg: z.string().min(1) }),
  'window/dirty': z.object({ dirty: z.boolean() }),
  'window/busy': z.object({ busy: z.boolean() }),
  'window/openWindow': z.object({ arg: z.unknown().optional() }),
  'window/close': z.object({}).loose(),
  /**
   * §4.1's `launch` group. §9.3's channel list is closed and has no `launch` channel, so these
   * ride the `window` channel — which is where "make a window appear" already lives (D-112).
   */
  'window/launchApp': z.object({ appId: z.string().min(1), payload: wirePayloadSchema.optional() }),
  'window/openWith': z.object({ path: z.string().min(1), appId: z.string().min(1).optional() }),
  'window/openedPath': z.object({}).loose(),
  /** The reply to a shell→app `closeRequested`. §18 F9: an app may veto its own close. */
  'window/closeReply': z.object({ to: z.string().min(1), decision: z.enum(['allow', 'deny']) }),

  'menu/publish': z.object({ tree: z.array(menuNodeWireSchema) }),

  'fs/call': z.object({ method: z.string().min(1), args: z.array(z.unknown()) }),

  'transfer/copy': z.object({ payload: wirePayloadSchema }),
  'transfer/readClipboard': z.object({}).loose(),
  'transfer/share': z.object({ payload: wirePayloadSchema }),

  /**
   * §4.1 gives `post` an `actions?: string[]`, and this table had grown a richer `{id, label}`
   * shape that no spec line asks for (D-138). A payload that is legal in process and rejected on
   * the wire is exactly the divergence §4.1's "every method async in both" exists to prevent, so
   * the wire follows the interface: the string is the label, and it is what comes back.
   */
  'notify/post': z.object({
    title: z.string().min(1),
    body: z.string().optional(),
    actions: z.array(z.string().min(1)).optional(),
  }),

  'dialog/alert': z.object({ message: z.string() }),
  'dialog/confirm': z.object({ message: z.string() }),
  'dialog/prompt': z.object({ message: z.string(), defaultValue: z.string().optional() }),

  'session/save': z.object({ blob: z.unknown() }),
  'session/load': z.object({}).loose(),

  /**
   * §11.2 / §9.3 — one frame of a running command.
   *
   * §9.3's table promises `run{cmd,args,cwd}` **⇒ `stdout`/`stderr`/`exit` frames** — a *stream*,
   * plural — and the wire had only a single `exec/reply`, which can carry exactly one answer. An
   * app streaming a minute of `git clone` output down a one-shot reply would have to buffer it and
   * send it at the end, which is the one thing a terminal must not do (D-166).
   *
   * `to` is the `exec/run` envelope's id, so several commands can run at once without their output
   * interleaving into the wrong tab. The stream ends at the `exit` frame.
   */
  'exec/frame': z.object({
    to: z.string().min(1),
    frame: z.discriminatedUnion('kind', [
      z.object({ kind: z.literal('stdout'), text: z.string() }),
      z.object({ kind: z.literal('stderr'), text: z.string() }),
      z.object({ kind: z.literal('exit'), code: z.number().int() }),
    ]),
  }),

  /** The app's immediate answer to `exec/run`: accepted, or refused with a reason. */
  'exec/reply': okOrError,

  /** §9.7 — a reserved chord the app caught and forwarded rather than acting on. */
  'keys/chord': z.object({ chord: z.string().min(1) }),
} as const;

// ── shell → app ────────────────────────────────────────────────────────────────────────────

export const SHELL_TO_APP = {
  'handshake/welcome': z.object({
    token: z.string().min(1),
    skin: z.string().min(1),
    theme: z.enum(['light', 'dark']),
    locale: z.string().min(1),
    /**
     * §4.1's `env().user`. §9.3's welcome list does not name it, and without it `env()` over the
     * bridge cannot answer the question §4.1 says it answers — an app would have to guess whether
     * it is talking to a guest (D-112).
     */
    user: z.union([
      z.object({ kind: z.literal('guest') }),
      z.object({ kind: z.literal('user'), displayName: z.string() }),
    ]),
    reservedChords: z.array(z.string().min(1)),
    permissions: z.array(z.string().min(1)),
  }),

  /** §18 F9 — vetoable. The app answers with `window/closeReply`. */
  'window/closeRequested': z.object({}).loose(),
  'window/focus': z.object({}).loose(),
  'window/blur': z.object({}).loose(),
  'window/resized': z.object({ w: z.number(), h: z.number() }),
  'window/themeChanged': z.object({ theme: z.enum(['light', 'dark']) }),
  'window/skinChanged': z.object({ skin: z.string().min(1), reservedChords: z.array(z.string()) }),

  /** §9.3 — a menu item, a jump-list entry, or a forwarded reserved chord coming back as an action. */
  'command/fire': z.object({ id: z.string().min(1) }),

  /**
   * Every channel an app can *call* on answers with `<channel>/reply` carrying `{to, ok, …}`.
   * §9.3's table writes this as "⇄" and "⇒" rather than listing them; the convention is uniform
   * so the client transport has one reply path rather than nine (D-112).
   */
  'window/reply': okOrError,
  'menu/reply': okOrError,

  'fs/reply': okOrError,
  /**
   * One `FsEvent`, pushed to the app that is watching for it.
   *
   * **Every field of `FsEvent` is here, and `subscription` besides** (D-127). A zod object drops
   * what it does not name, so a schema narrower than the type it carries is not a stricter check —
   * it is a silent truncation: a `move` would arrive with no `from`, and an app holding two
   * watches could not tell which of them fired.
   */
  'fs/changed': z.object({
    kind: z.string().min(1),
    path: z.string().min(1),
    /** Present for `move` — where it came from. */
    from: z.string().min(1).optional(),
    entry: fileEntryWireSchema.optional(),
    /** Which `fs.watch` this belongs to; an app may hold several at once. */
    subscription: z.string().min(1).optional(),
  }),

  'transfer/reply': okOrError,
  'transfer/receive': z.object({ payload: wirePayloadSchema, payloadToken: z.string().min(1) }),

  'notify/reply': okOrError,
  'notify/action': z.object({ id: z.string().min(1), action: z.string().min(1) }),

  'dialog/reply': okOrError,

  'session/state': z.object({ to: z.string().min(1), blob: z.unknown() }),
  'session/reply': okOrError,

  /** D-022 — the signed SSO handoff. */
  'auth/ticket': z.object({ jwt: z.string().min(1) }),

  /** §11.2 — the terminal's `exec.provide` frames. */
  'exec/run': z.object({
    cmd: z.string().min(1),
    args: z.array(z.string()),
    cwd: z.string().optional(),
  }),
} as const;

export type AppToShellType = keyof typeof APP_TO_SHELL;
export type ShellToAppType = keyof typeof SHELL_TO_APP;

export type Direction = 'appToShell' | 'shellToApp';

const TABLES: Record<Direction, Record<string, z.ZodTypeAny>> = {
  appToShell: APP_TO_SHELL,
  shellToApp: SHELL_TO_APP,
};

/** Every `<channel>/<type>` key a direction accepts. */
export function messageKeys(direction: Direction): string[] {
  return Object.keys(TABLES[direction]).sort();
}

/** The channels a direction actually carries — `auth` is shell→app only, `keys` app→shell only. */
export function channelsFor(direction: Direction): Channel[] {
  const found = new Set<Channel>();
  for (const key of Object.keys(TABLES[direction])) found.add(key.split('/')[0] as Channel);
  return [...found].sort();
}

export interface DecodedMessage {
  channel: Channel;
  type: string;
  key: string;
  payload: unknown;
  envelope: Envelope;
}

export type DecodeFailure =
  /** Not an envelope at all — the wrong shape, a bad version, an unknown channel. */
  | { ok: false; reason: 'envelope'; detail: string }
  /** A well-formed envelope naming a `(ch, type)` this direction does not carry. */
  | { ok: false; reason: 'unknown-message'; detail: string }
  /** The right message, with a payload that is not what it should be. */
  | { ok: false; reason: 'payload'; detail: string };

export type DecodeResult = ({ ok: true } & DecodedMessage) | DecodeFailure;

/**
 * Validate one raw `MessageEvent.data`. **Never throws** — §9.3 requires a bad frame to be dropped
 * and counted, and an exception on the message path would be a way to stop the shell listening.
 */
export function decodeMessage(raw: unknown, direction: Direction): DecodeResult {
  const envelope = envelopeSchema.safeParse(raw);
  if (!envelope.success) {
    return {
      ok: false,
      reason: 'envelope',
      detail: envelope.error.issues.map((i) => `${i.path.join('.') || '<root>'}: ${i.message}`).join('; '),
    };
  }

  const key = `${envelope.data.ch}/${envelope.data.type}`;
  const schema = TABLES[direction][key];
  if (!schema) {
    return { ok: false, reason: 'unknown-message', detail: `${direction} does not carry '${key}'` };
  }

  const payload = schema.safeParse(envelope.data.payload);
  if (!payload.success) {
    return {
      ok: false,
      reason: 'payload',
      detail: `${key}: ${payload.error.issues
        .map((i) => `${i.path.join('.') || '<root>'}: ${i.message}`)
        .join('; ')}`,
    };
  }

  return {
    ok: true,
    channel: envelope.data.ch,
    type: envelope.data.type,
    key,
    payload: payload.data,
    envelope: envelope.data,
  };
}

/** Build an envelope. `id` is the caller's, so a reply can name what it answers. */
export function envelope(
  id: string,
  key: AppToShellType | ShellToAppType,
  payload: unknown,
  token: string,
): Envelope {
  const slash = key.indexOf('/');
  return {
    v: BRIDGE_PROTOCOL_VERSION,
    id,
    ch: key.slice(0, slash) as Channel,
    type: key.slice(slash + 1),
    payload,
    token,
  };
}
